import { FunctionComponent, ImgHTMLAttributes, useState } from "react";

export type HideOnErrorImgProps = Omit<ImgHTMLAttributes<HTMLImageElement>, "onError" | "src"> & {
	/** A missing source renders nothing, so a cell can pass a nullable logo URL straight through. */
	src?: string | null;
};

/**
 * An image that disappears when its source fails to load — a company logo or avatar URL that
 * 404s, which is common for scraped data.
 *
 * It hides itself through React state, never by removing its own DOM node (PREC-4386). React still
 * owns that node: `onError={(e) => e.currentTarget.parentNode.removeChild(e.currentTarget)}`, which
 * thirteen table cells used, left React's tree pointing at a detached element, so the next
 * re-render or unmount (a tab switch, a polling refetch) threw "Failed to execute 'removeChild' on
 * 'Node'" and the whole list table fell into the error boundary — 46 production events on the list
 * detail page (Bugsink SIGNALIST-53).
 *
 * The failed source is remembered rather than a boolean, so the image comes back when the row
 * behind the cell changes to one with a working URL.
 */
export const HideOnErrorImg: FunctionComponent<HideOnErrorImgProps> = ({ src, ...rest }) => {
	const [failedSrc, setFailedSrc] = useState<string | null>(null);

	if (!src || failedSrc === src) {
		return null;
	}

	return <img {...rest} src={src} onError={() => setFailedSrc(src)} />;
};
